"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";
import { GROQImage } from "@/sanity/lib/types";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { GROQCornerButton } from "@/sanity/lib/definitions";
import { motion } from "framer-motion";
import UnicornWrapper from "@/components/global/utils/UnicornWrapper";
import DecorationLine from "@/components/global/ui/DecorationLine";

import halfCircle from "@/public/icons/half-circle.svg";
import unicornPlaceholder from "@/public/global/footer-placeholder.png";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function LastCTA({
  chip,
  text,
  textPadding,
  cta,
  className = "",
  bannerText,
  showTags = false,
  tags,
  bullets,
  subtext,
  hideDecorationLines = false,
  id = "",
}: {
  chip?: {
    icon?: GROQImage;
    text: string;
  };
  text: PortableTextBlock[];
  textPadding?: string;
  cta: GROQCornerButton;
  className?: string;
  bannerText?: string;
  showTags?: boolean;
  tags?: string[];
  /** marker list, for the designs that spell the offer out instead of tagging it */
  bullets?: string[];
  subtext?: PortableTextBlock[];
  hideDecorationLines?: boolean;
  id?: string;
}) {
  if (!text) return null;

  return (
    <div
      id={id ?? ""}
      className={cn("relative overflow-clip bg-[#0A1A1D]", className)}>
      {!hideDecorationLines && (
        <>
          <DecorationLine
            className="top-0"
            orientation="vertical"
            position="left"
            style="light"
          />
          <DecorationLine
            className="top-0"
            orientation="vertical"
            position="right"
            style="light"
          />
        </>
      )}

      <Image
        className="absolute top-0 -left-[13rem] !w-[48rem] lg:left-0 lg:!w-[80rem]"
        src={halfCircle}
        alt=""
      />
      <Image
        className="absolute -right-[13rem] bottom-0 !w-[48rem] rotate-180 lg:right-0 lg:!w-[80rem]"
        src={halfCircle}
        alt=""
      />

      <div className="relative container flex flex-col items-center py-[8rem] lg:py-[16rem]">
        <div className="w-[min(187.75rem,100%)] border-t-[1rem] border-t-[#D9F9B9]/10">
          <div
            className={cn(
              "bg-[#D9F9B9] px-[3rem] pt-[3rem] sm:px-[5rem] sm:pt-[5rem] lg:py-0 lg:pl-[11rem] lg:pr-[8rem] flex flex-col items-center lg:flex-row lg:justify-between gap-[1rem]",
              "[clip-path:polygon(0_0,100%_0,100%_calc(100%-6rem),calc(100%-6rem)_100%,0_100%)] lg:[clip-path:polygon(0_0,100%_0,100%_calc(100%-9rem),calc(100%-9rem)_100%,0_100%)]",
            )}>
            <div className={cn("flex flex-col items-start lg:py-[10.5rem] w-[min(99rem,100%)]", textPadding)}>
              {chip?.text && (
                <motion.div {...fadeInUp()}>
                  <Chip
                    imageBG="bg-[#0A1A1D] prose-img:!w-[3rem]"
                    image={chip?.icon}
                    text={chip.text}
                  />
                </motion.div>
              )}

              <motion.div
                {...fadeInUp(0.1)}
                className="richtext richtext-start mb-[6rem] w-[74rem] prose-strong:font-medium prose-strong:text-black prose-em:font-ivy">
                <PortableText
                  value={text}
                  components={richTextComponent}
                />
              </motion.div>

              {showTags && tags && tags.length > 0 && (
                <motion.div
                  className="mt-[2rem] flex flex-wrap gap-[0.75rem]"
                  {...fadeInUp(0.15)}>
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center justify-center rounded-full border border-black/50 px-[1.5rem] py-[1rem] text-[1.8rem] leading-none text-[#0A1A1D]/80">
                      {tag}
                    </span>
                  ))}
                </motion.div>
              )}

              {bullets && bullets.length > 0 && (
                /* className lives on an inner div: this framer-motion version's
                   types reject it on the motion component itself */
                <motion.div {...fadeInUp(0.15)}>
                  <div className="mt-[2rem] flex flex-col items-start gap-[1rem]">
                    {bullets.map((bullet, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-[1rem]">
                        {/* the cut-corner marker the new theme uses for lists */}
                        <span
                          className="block h-[1.5rem] w-[1.5rem] shrink-0 bg-[#112628] [clip-path:polygon(50%_0,100%_50%,100%_100%,0_100%,0_0)]"
                          aria-hidden
                        />
                        <p className="m-0 font-geist text-[2.25rem] leading-none font-normal text-[#0A1A1D] opacity-60">
                          {bullet}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {subtext && (
                <motion.div
                  {...fadeInUp(0.18)}
                  className="richtext richtext-start mt-[2rem] prose-strong:font-medium prose-strong:text-black prose-em:font-ivy">
                  <PortableText
                    value={subtext}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {cta && (
                <motion.div
                  className="w-full sm:w-auto"
                  {...fadeInUp(0.2)}>
                  <CTACornerButton
                    className="mt-[4rem]"
                    data={cta}
                  />
                </motion.div>
              )}
            </div>

            <div className="relative aspect-square w-[min(35rem,100%)] flex-shrink-0 opacity-40 sm:w-[min(50rem,100%)] lg:w-[min(60rem,100%)] [&_canvas]:!h-full [&_canvas]:!w-full">
              <UnicornWrapper
                jsonFilePath="unicorn-studio/lastCTA-logo.json"
                placeholderImage={unicornPlaceholder}
              />
            </div>
          </div>
        </div>
      </div>

      {bannerText && <ScrollingBanner text={bannerText} />}
    </div>
  );
}
